import type { matches as ru } from '../ru/matches';
import { pluralEn } from '../../plural';

const playersCount = (n: number): string => `${n} ${pluralEn(n, 'player', 'players')}`;

export const matches: typeof ru = {
  list: {
    title: 'Matches',
    searchPlaceholder: 'Player name or match ID',
    playerSearchError: 'Failed to search players.',
    loadError: 'Failed to load matches.',
    hoursMinutes: (hours: number, minutes: number): string => `${hours}h ${minutes}m`,
    minutes: (minutes: number): string => `${minutes}m`,
    live: 'live',
    sort: {
      newest: 'Newest',
      oldest: 'Oldest',
      mostRounds: 'Most rounds',
      fewestRounds: 'Fewest rounds'
    },
    sortLabel: 'Sort',
    sortOption: (label: string): string => `Sort: ${label}`,
    rosterLabel: 'Match roster',
    playersCount,
    statusEnded: 'Finished',
    statusLive: 'Live',
    heroOfPlayer: (nick: string): string => `Hero · ${nick}`,
    filters: {
      statusAll: 'Status: All',
      statusEnded: 'Finished',
      statusActive: 'Live',
      typeAll: 'Type: All',
      startAll: 'Start: All time',
      today: 'Today',
      days7: 'Last 7 days',
      days30: 'Last 30 days'
    },
    clearPlayerFilter: 'Clear player filter',
    playerChip: (name: string): string => `Player: ${name}`,
    chips: {
      active: 'Live',
      endedToday: 'Finished today',
      withMe: 'With you'
    },
    resetFilters: 'Reset filters',
    newMatches: (n: number): string => `${n} new ${pluralEn(n, 'match', 'matches')} · refresh`,
    columns: {
      type: 'Type',
      startDuration: 'Start · Dur.',
      myHero: 'Your hero',
      playerHero: "Player's hero",
      players: 'Players',
      winner: 'Winner',
      round: 'Round',
      status: 'Status'
    },
    loadingLabel: 'Loading matches',
    emptyTitle: 'No matches found',
    emptyHint: 'Try changing the filters',
    openMatch: (id: string): string => `Open match ${id}`,
    pageInfo: (page: number, totalPages: number, total: number): string =>
      `Page ${page} / ${totalPages} · ${total} total`,
    prev: '← Back',
    next: 'Next →'
  },
  mine: {
    loadError: 'Failed to load your matches.',
    kicker: 'Personal',
    title: 'My matches',
    total: (n: number): string => `${n} total`
  },
  detail: {
    loadError: 'Failed to load the match.',
    stateLoadError: 'Failed to load the match state.',
    hoursMinutes: (hours: number, minutes: number): string => `${hours}h ${minutes}m`,
    minutes: (minutes: number): string => `${minutes}m`,
    playersCount,
    loadingMatch: 'Loading match...',
    notFoundTitle: 'Match not found',
    notFoundDescription: 'No match exists with this ID.',
    toMatchList: 'To matches',
    backToFeed: '← Back to matches',
    statusEnded: 'Finished',
    statusLive: 'Live',
    matchEnded: 'Match finished',
    sendCommandTitle: 'Send a command to the match',
    command: 'Command',
    copyId: 'Copy ID',
    copyMatchId: 'Copy match ID',
    kpi: {
      start: 'Start',
      end: 'End',
      duration: 'Duration',
      round: 'Round',
      place: 'Place',
      hero: 'Hero',
      rating: 'Rating',
      duelsWl: 'Duels W/L',
      eliminatedRound: 'Eliminated in round'
    },
    yourResult: 'Your result',
    bansHeroes: 'Heroes',
    bansAbilities: 'Abilities',
    loadingBans: 'Loading bans...',
    playersPanelTitle: 'Players and places',
    playersPanelSub: (n: number): string => `${n} ${pluralEn(n, 'participant', 'participants')} · by current place`,
    stateErrorTitle: 'State error',
    noPlayers: 'No players',
    chat: {
      title: 'Match chat',
      loading: 'Loading chat...',
      errorTitle: 'Chat error',
      loadError: 'Failed to load the match chat.',
      empty: 'No messages',
      teamTag: 'Team',
      ariaLabel: 'Match chat messages',
      round: (round: number): string => `R${round}`
    }
  },
  bansStrip: {
    heroes: 'Hero bans',
    abilities: 'Ability bans'
  },
  commandModal: {
    choose: '— select —',
    choosePlayer: '— select a player —',
    playerUid: 'Player UID',
    catalogLoadError: 'Failed to load the command catalog.',
    chooseType: 'Select a command type.',
    fieldRequired: (label: string): string => `The "${label}" field is required.`,
    sent: 'Command sent.',
    sendError: 'Failed to send the command.',
    ariaLabel: 'Send match command',
    kicker: 'ADMINISTRATOR',
    title: (matchId?: string): string => `Match command${matchId ? ` · ${matchId}` : ''}`,
    loadingCatalog: 'Loading command catalog…',
    commandType: 'Command type',
    chooseCommand: '— select a command —',
    catalogEmpty: 'The command catalog is empty.',
    sending: 'Sending…',
    send: 'Send'
  },
  detailHeader: {
    kicker: 'Match',
    statusEnded: 'Finished',
    statusActive: 'Live',
    stats: {
      type: 'Type',
      round: 'Round',
      players: 'Players',
      start: 'Start',
      finish: 'Finish',
      duration: 'Duration'
    },
    message: 'Message',
    toList: 'To matches'
  },
  table: {
    columns: {
      type: 'Type',
      status: 'Status',
      roster: 'Roster',
      start: 'Start',
      finish: 'Finish'
    },
    loading: 'Loading matches…',
    empty: 'No matches match the current filters.',
    openMatch: (id: string): string => `Open match ${id}`,
    playersCount: (n: number): string => `${n} ${pluralEn(n, 'player', 'players')}`,
    statusEnded: 'Finished',
    statusActive: 'Live',
    next: 'Next',
    pageInfo: (page: number, totalPages: number, remaining: number, total: number): string =>
      `Page ${page} / ${totalPages}, ${remaining} left, ${total} total`
  },
  filters: {
    title: 'Matches',
    total: (n: number): string => `${n} total`,
    allStatuses: 'All statuses',
    active: 'Live',
    ended: 'Finished',
    start: 'Start',
    finish: 'Finish',
    startFrom: 'Start from',
    startTo: 'Start to',
    finishFrom: 'Finish from',
    finishTo: 'Finish to'
  },
  playersTable: {
    stash: 'Stash',
    extraStash: 'Extra stash',
    loading: 'Loading match state…',
    noPlayers: 'This match has no players yet.',
    players: 'Players',
    stateNotSaved: 'Match state was not saved',
    hideStash: 'Hide stashes',
    showStash: 'Show stashes',
    columns: {
      place: 'Place',
      player: 'Player',
      level: 'Level',
      gold: 'Gold',
      duel: 'Duel',
      hero: 'Hero',
      abilities: 'Abilities',
      inventory: 'Inventory'
    }
  },
  stateCard: {
    ratingDelta: 'Rating change for the match',
    profileTitle: (name: string): string => `Profile: ${name}`,
    profileAria: (name: string): string => `Player profile ${name}`,
    level: (level: number | string): string => `· lvl ${level}`,
    zones: {
      abilities: 'abilities',
      items: 'items',
      backpack: 'backpack',
      stash: 'stash',
      extraStash: 'extra stash'
    },
    goldTitle: 'Gold',
    gold: 'gold',
    duelScore: 'Duel score (wins : losses)'
  },
  selectedPanel: {
    place: (place: number): string => `Place ${place}`,
    kicker: 'Context',
    title: 'Selected match',
    statusEnded: 'Finished',
    statusActive: 'Live',
    type: 'Type',
    players: 'Players',
    start: 'Start',
    finish: 'Finish',
    roster: 'Roster',
    playersCount: (n: number): string => `${n} ${pluralEn(n, 'player', 'players')}`,
    matchState: 'Match state',
    sendMessage: 'Send message',
    failedPrefix: 'Failed to',
    empty: 'Select a match in the table to open its context.'
  }
};
